import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { useQuery } from "@tanstack/react-query";

import { CtaBand, PageHero } from "@/components/site/Page";
import { ReviewGrid } from "@/components/site/Testimonials";
import type { Locale } from "@/lib/i18n";
import { listPublishedTestimonials } from "@/lib/testimonials.functions";
import type { Testimonial } from "@/lib/testimonials";

export const REVIEWS_COPY: Record<
  Locale,
  {
    eyebrow: string;
    title: string;
    intro: string;
    average: string;
    count: string;
    empty: string;
    metaTitle: string;
    metaDescription: string;
    ctaTitle: string;
    ctaText: string;
  }
> = {
  sv: {
    eyebrow: "Röster från familjer",
    title: "Omdömen från familjer på skolan",
    intro:
      "Här samlar vi omdömen som familjer själva har formulerat eller valt, redigerat och godkänt för publicering. Det är alltid familjen som bestämmer den slutliga texten.",
    average: "Snittbetyg",
    count: "publicerade omdömen",
    empty: "Just nu finns inga publicerade omdömen på svenska.",
    metaTitle: "Omdömen från familjer | Swedish International School",
    metaDescription:
      "Läs omdömen från familjer på Swedish International School i Estepona – Marbella. Publicerade med familjernas eget godkännande.",
    ctaTitle: "Vill du se skolan på riktigt?",
    ctaText: "Boka ett besök eller gör en anmälan – vi visar gärna runt och svarar på alla frågor.",
  },
  en: {
    eyebrow: "Family voices",
    title: "Reviews from families at the school",
    intro:
      "Reviews written by families, or selected and edited by them, and published with their consent. The family always decides the final wording.",
    average: "Average rating",
    count: "published reviews",
    empty: "There are no published reviews in English right now.",
    metaTitle: "Reviews from families | Swedish International School",
    metaDescription:
      "Read reviews from families at Swedish International School in Estepona – Marbella, published with their own consent.",
    ctaTitle: "Would you like to see the school for yourself?",
    ctaText: "Book a visit or send an application – we are happy to show you around and answer your questions.",
  },
  es: {
    eyebrow: "Voces de las familias",
    title: "Opiniones de las familias del colegio",
    intro:
      "Opiniones redactadas por las familias, o elegidas y editadas por ellas, y publicadas con su consentimiento. La familia siempre decide el texto final.",
    average: "Valoración media",
    count: "opiniones publicadas",
    empty: "Ahora mismo no hay opiniones publicadas en español.",
    metaTitle: "Opiniones de las familias | Swedish International School",
    metaDescription:
      "Lee las opiniones de las familias del Swedish International School en Estepona – Marbella, publicadas con su consentimiento.",
    ctaTitle: "¿Quieres conocer el colegio?",
    ctaText: "Reserva una visita o envía una solicitud: te enseñamos el colegio y resolvemos tus dudas.",
  },
};

/** Snitt och antal räknas bara på riktiga omdömen, aldrig demodata. */
export function reviewStats(reviews: Testimonial[]) {
  const real = reviews.filter((r) => !r.isTest);
  const count = real.length;
  const average = count ? real.reduce((sum, r) => sum + r.rating, 0) / count : 0;
  return { real, count, average: Math.round(average * 10) / 10 };
}

const HIDDEN_COPY: Record<Locale, string> = {
  sv: "Omdömessidan öppnar när fler familjer har hunnit lämna sitt omdöme. Under tiden hittar du utvalda röster på startsidan.",
  en: "The reviews page opens once more families have shared their review. In the meantime you will find selected voices on the start page.",
  es: "La página de opiniones se abrirá cuando más familias hayan compartido su opinión. Mientras tanto, encontrarás algunas voces en la página de inicio.",
};

export function ReviewsPage({
  locale,
  initialReviews,
  enabled = true,
}: {
  locale: Locale;
  /** Omdömen från routens loader – gör att sidan renderas färdig direkt (ingen layoutförskjutning). */
  initialReviews?: Testimonial[];
  /** Av i admin tills vi har tillräckligt många omdömen. */
  enabled?: boolean;
}) {
  const copy = REVIEWS_COPY[locale];
  const { data } = useQuery({
    queryKey: ["testimonials", "published", "page", locale],
    queryFn: () => listPublishedTestimonials({ data: { limit: 24, locale } }),
    staleTime: 5 * 60 * 1000,
    ...(initialReviews ? { initialData: initialReviews } : {}),
  });

  const reviews = data ?? initialReviews ?? [];

  const { count, average } = reviewStats(reviews);

  if (!enabled) {
    return (
      <>
        <PageHero eyebrow={copy.eyebrow} title={copy.title} intro={HIDDEN_COPY[locale]} />
        <CtaBand title={copy.ctaTitle} text={copy.ctaText} />
      </>
    );
  }

  return (
    <>
      <PageHero eyebrow={copy.eyebrow} title={copy.title} intro={copy.intro} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
        {count > 0 ? (
          <div className="mb-10 flex flex-wrap items-baseline gap-x-8 gap-y-2 border-b border-border pb-6">
            <p className="flex items-baseline gap-2">
              <span className="font-display text-4xl font-semibold text-foreground">
                {average.toLocaleString(locale === "sv" ? "sv-SE" : locale === "es" ? "es-ES" : "en-GB")}
              </span>
              <span className="text-sm text-muted-foreground">{copy.average} / 5</span>
            </p>
            <p className="text-sm text-muted-foreground">
              {count} {copy.count}
            </p>
          </div>
        ) : null}

        {reviews.length ? (
          <ReviewGrid reviews={reviews} locale={locale} />
        ) : (
          <p className="text-sm text-muted-foreground">{copy.empty}</p>
        )}
      </section>

      <FaqBlock items={faqFor("omdomen", locale)} locale={locale} />

      <CtaBand title={copy.ctaTitle} text={copy.ctaText} />
    </>
  );
}
